import React from 'react';
import { Utensils, Wifi, Fan, Car, Users, UtensilsCrossed, Video, Droplets, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from './ui/card';
import { images } from '../data/mockData';

const Amenities = () => {
  const { language } = useLanguage();

  const amenities = [
    {
      icon: Video,
      title: { en: 'CCTV Surveillance', bn: 'সিসিটিভি নিরাপত্তা' },
      description: { 
        en: 'CCTV equipped homestay for your safety', 
        bn: 'আপনার নিরাপত্তার জন্য সিসিটিভি সজ্জিত হোমস্টে' 
      },
      color: 'from-gray-600 to-gray-800'
    },
    {
      icon: Sparkles,
      title: { en: 'Spotless Clean', bn: 'পরিষ্কার পরিচ্ছন্ন' },
      description: { 
        en: 'Clean toilets, fresh bedsheets & blankets like home', 
        bn: 'পরিষ্কার টয়লেট, তাজা বিছানার চাদর এবং কম্বল ঘরের মতো' 
      },
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Utensils,
      title: { en: 'Kitchen Facility', bn: 'রান্নাঘর সুবিধা' },
      description: { 
        en: 'Cook your own meals in our fully equipped kitchen', 
        bn: 'আমাদের সম্পূর্ণ সজ্জিত রান্নাঘরে নিজের খাবার রান্না করুন' 
      },
      color: 'from-red-500 to-orange-500'
    },
    {
      icon: Droplets,
      title: { en: 'Free RO Water', bn: 'ফ্রি RO জল' },
      description: { 
        en: 'Pure RO filtered drinking water available', 
        bn: 'বিশুদ্ধ RO ফিল্টার করা পানীয় জল উপলব্ধ' 
      },
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: UtensilsCrossed,
      title: { en: 'Bengali Food', bn: 'বাঙালি খাবার' },
      description: { 
        en: 'Authentic Bengali meals available on request', 
        bn: 'অনুরোধে খাঁটি বাঙালি খাবার পাওয়া যায়' 
      },
      color: 'from-green-500 to-teal-500'
    },
    {
      icon: Fan,
      title: { en: 'AC Rooms', bn: 'এসি রুম' },
      description: { 
        en: 'Air-conditioned rooms with geyser', 
        bn: 'গিজার সহ শীতাতপ নিয়ন্ত্রিত রুম' 
      },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Wifi,
      title: { en: 'Free Wi-Fi', bn: 'ফ্রি ওয়াই-ফাই' },
      description: { 
        en: 'High-speed internet connectivity', 
        bn: 'উচ্চ গতির ইন্টারনেট সংযোগ' 
      },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Car,
      title: { en: 'Bus Tours', bn: 'বাস ট্যুর' },
      description: { 
        en: 'Organized Brij temple tours by bus', 
        bn: 'বাসে সংগঠিত ব্রজ মন্দির ট্যুর' 
      },
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Users,
      title: { en: 'Group Packages', bn: 'গ্রুপ প্যাকেজ' },
      description: { 
        en: 'Special packages for Bengali groups', 
        bn: 'বাঙালি গ্রুপের জন্য বিশেষ প্যাকেজ' 
      },
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  return (
    <section id="amenities" className="py-20 bg-gradient-to-b from-white to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="h-px w-12 bg-orange-400" />
              <div className="w-2 h-2 rotate-45 bg-orange-400" />
              <div className="h-px w-12 bg-orange-400" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              {language === 'bn' ? 'সুবিধাসমূহ' : 'Amenities & Facilities'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'bn' 
                ? 'আপনার আরামদায়ক থাকার জন্য সবকিছু' 
                : 'Everything for your comfortable stay'}
            </p>
          </div>

          {/* Amenities Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {amenities.map((amenity, index) => (
              <Card 
                key={index}
                className="border-2 border-orange-100 hover:border-orange-300 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white group"
              >
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br ${amenity.color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    <amenity.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {amenity.title[language]}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {amenity.description[language]}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Special Section: Bengali Group Tours */}
          <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 via-white to-orange-50 shadow-xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image Side */}
                <div className="relative h-80 md:h-auto">
                  <img
                    src={images.facilities[1]}
                    alt="Bengali Group Tour"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                {/* Content Side */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-6 shadow-lg">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">
                    {language === 'bn' 
                      ? 'বাঙালি গ্রুপ ট্যুর প্যাকেজ' 
                      : 'Bengali Group Tour Package'}
                  </h3>
                  
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {language === 'bn'
                      ? 'আমরা বাঙালি গ্রুপ ট্যুরের জন্য বিশেষ ব্যবস্থা করি যার মধ্যে রয়েছে:'
                      : 'We host special Bengali group tours with complete arrangements including:'}
                  </p>

                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        {language === 'bn' ? 'খাঁটি বাঙালি খাবারের ব্যবস্থা' : 'Authentic Bengali food arrangements'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        {language === 'bn' ? 'আরামদায়ক থাকার ব্যবস্থা' : 'Comfortable accommodation'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        {language === 'bn' ? 'বাসে ব্রজ মন্দির ভ্রমণ' : 'Brij temple tour by bus'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">
                        {language === 'bn' ? 'বাংলা ভাষায় গাইড সেবা' : 'Bengali-speaking guide service'}
                      </span>
                    </li>
                  </ul>

                  <p className="text-orange-600 font-semibold">
                    {language === 'bn'
                      ? '📞 গ্রুপ বুকিংয়ের জন্য আমাদের সাথে যোগাযোগ করুন'
                      : '📞 Contact us for group bookings'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Kitchen Facility Images */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl group">
              <img
                src={images.facilities[0]}
                alt="Kitchen Facility"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div>
                  <h4 className="text-white font-bold text-xl mb-1">
                    {language === 'bn' ? 'রান্নাঘর সুবিধা' : 'Kitchen Facility'}
                  </h4>
                  <p className="text-orange-200 text-sm">
                    {language === 'bn' 
                      ? 'নিজের খাবার রান্না করুন' 
                      : 'Cook your own meals'}
                  </p>
                </div>
              </div>
            </div>
            <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl group">
              <img
                src={images.facilities[1]}
                alt="Group Gathering"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div>
                  <h4 className="text-white font-bold text-xl mb-1">
                    {language === 'bn' ? 'গ্রুপ সমাবেশ' : 'Group Gatherings'}
                  </h4>
                  <p className="text-orange-200 text-sm">
                    {language === 'bn' 
                      ? 'পরিবার এবং গ্রুপের জন্য আদর্শ' 
                      : 'Perfect for families and groups'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Amenities;
